import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import { RankStatus } from "../../../common/data/service-proxies";
import GrowthIndicator from "./GrowthIndicator";
import LineGraph from "./LineGraph";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import RemoveIcon from "@material-ui/icons/Remove";

export interface PerformerRowBase {
  vehicleName?: string | null;
  driverFullName?: string | null;
  growth?: number;
  isGrowing?: boolean;
  stops?: number[] | null;
  rankStatus?: RankStatus;
}

export interface PerformersTableProps<TRow extends PerformerRowBase> {
  rows: TRow[];
  valueColumnHeader: string;
  renderValueCell: (row: TRow) => React.ReactNode;
  getRowKey: (row: TRow, index: number) => string;
  truckLabelKey?: string;
  isReverse?: boolean;
}

export function PerformersTable<TRow extends PerformerRowBase>({
  rows,
  valueColumnHeader,
  renderValueCell,
  getRowKey,
  truckLabelKey = "common.truck",
  isReverse = false,
}: PerformersTableProps<TRow>): React.ReactElement {
  const translate = useTranslate();

  return (
    <TableContainer style={{ flex: 1, overflow: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate(truckLabelKey, { _: "Truck" })}</TableCell>
            <TableCell>
              {translate(valueColumnHeader, { _: valueColumnHeader })}
            </TableCell>
            <TableCell>
              {translate("fuelEconomyReport.performance", {
                _: "Performance",
              })}
            </TableCell>
            <TableCell>
              {translate("fuelEconomyReport.rank", { _: "Rank" })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={getRowKey(row, index)}>
              <TableCell>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" color="textSecondary">
                    {translate(truckLabelKey, { _: "Truck" })} #{" "}
                    {row.vehicleName ?? "-"}
                  </Typography>
                  <Typography variant="body2">
                    {row.driverFullName ?? "-"}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{renderValueCell(row)}</TableCell>
              <TableCell>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box width={80}>
                    <GrowthIndicator
                      growth={row.growth ?? undefined}
                      isGrowing={row.isGrowing ?? false}
                      isReverse={isReverse}
                    />
                  </Box>
                  <Box width={200}>
                    <LineGraph
                      data={row.stops ?? undefined}
                      isGrowing={row.isGrowing ?? false}
                      width={200}
                      height={48}
                      isReverse={isReverse}
                    />
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body1"
                    style={{ fontWeight: 600, marginRight: 8 }}
                  >
                    {index + 1}
                  </Typography>
                  {row.rankStatus === RankStatus.Up && (
                    <ArrowUpwardIcon
                      style={{ color: "#ef4444", fontSize: 18 }}
                    />
                  )}
                  {row.rankStatus === RankStatus.Down && (
                    <ArrowDownwardIcon
                      style={{ color: "#10b981", fontSize: 18 }}
                    />
                  )}
                  {row.rankStatus === RankStatus.Idle && (
                    <RemoveIcon style={{ color: "#3b82f6", fontSize: 18 }} />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default PerformersTable;
